import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Form, Input, Select, Button, Row, Col, Typography, Switch, message, Upload } from 'antd';
import { SaveOutlined, ArrowLeftOutlined, UploadOutlined } from '@ant-design/icons';
import ImgCrop from 'antd-img-crop';
import pagesService from '../../services/pagesService';
import templatesService from '../../services/templatesService';
import masterService from '../../services/masterService';
import { useCommon } from '../../context/CommonContext';
import { useNotify } from '../../context/NotificationContext';
import { getImageUrl } from '../../utils/commonFunctions';
const { Title, Text } = Typography;
const { TextArea } = Input;

const PAGE_TYPES = [
  'TXT','HTML','JAVASCRIPT','JSON','XML','CSS','COLLECTIONS','DYNAMIC'
];

const EditPage = () => {
  const { page_id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [seoImage, setSeoImage] = useState(null);
  const [ogImage, setOgImage] = useState(null);
  const [twitterImage, setTwitterImage] = useState(null);
  const { setPageTitle } = useCommon();
  const notify = useNotify();

  useEffect(() => {
    setPageTitle("Edit Page");
  }, []);

  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const res = await templatesService.getTemplates({ isAll: 1, limit: 1000 });
        const list = res?.data?.data?.data || res?.data?.data || res?.data || [];
        setTemplates(list);
      } catch (_) {}
    };
    loadTemplates();
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await pagesService.getById(page_id);
        const data = res?.data?.data || res?.data || {};
        // Parse SEO JSON if stored as string
        let seo = {};
        try { seo = typeof data.page_seo_meta === 'string' ? JSON.parse(data.page_seo_meta) : (data.page_seo_meta || {});} catch(_){}
        
        // Convert keywords strings to arrays for Select component
        const seoKeywordsArray = seo.seo_keywords
          ? (Array.isArray(seo.seo_keywords)
              ? seo.seo_keywords
              : String(seo.seo_keywords).split(',').map(k => k.trim()).filter(Boolean))
          : [];
        const ogKeywordsArray = seo.og_keywords
          ? (Array.isArray(seo.og_keywords)
              ? seo.og_keywords
              : String(seo.og_keywords).split(',').map(k => k.trim()).filter(Boolean))
          : [];
        const twitterKeywordsArray = seo.twitter_keywords
          ? (Array.isArray(seo.twitter_keywords)
              ? seo.twitter_keywords
              : String(seo.twitter_keywords).split(',').map(k => k.trim()).filter(Boolean))
          : [];
        
        form.setFieldsValue({
          title: data.title,
          content: data.content,
          page_type: data.page_type,
          slug: data.slug,
          collection_id: data.collection_id,
          fk_template_id: data.fk_template_id,
          show_header: !!data.show_header,
          show_footer: !!data.show_footer,
          is_custom_link: !!data.is_custom_link,
          is_active: !!data.is_active,
          seo_title: seo.seo_title,
          seo_description: seo.seo_description,
          seo_image: seo.seo_image,
          seo_keywords: seoKeywordsArray,
          og_title: seo.og_title,
          og_description: seo.og_description,
          og_image: seo.og_image,
          og_keywords: ogKeywordsArray,
          twitter_title: seo.twitter_title,
          twitter_description: seo.twitter_description,
          twitter_image: seo.twitter_image,
          twitter_keywords: twitterKeywordsArray,
        });
        setSeoImage(seo.seo_image || null);
        setOgImage(seo.og_image || null);
        setTwitterImage(seo.twitter_image || null);
      } catch (e) {
        message.error('Failed to load page');
      }
    };
    load();
  }, [page_id, form]);

  // Upload handler for SEO Images
  const handleSeoImageUpload = async (file, fieldName) => {
    try {
      const formData = new FormData();
      formData.append("image_type", "images");
      formData.append("folder_name", "pages");
      formData.append("image", file);
      const response = await masterService.uploadImage(formData);
      const s3Key = response.data?.data?.s3_key;
      if (s3Key) {
        form.setFieldsValue({ [fieldName]: s3Key });
        if (fieldName === "seo_image") setSeoImage(s3Key);
        if (fieldName === "og_image") setOgImage(s3Key);
        if (fieldName === "twitter_image") setTwitterImage(s3Key);
        notify.success({ message: "Image uploaded successfully" });
      }
    } catch (error) {
      notify.error({ message: "Failed to upload image" });
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const seoKeywords = Array.isArray(values.seo_keywords)
        ? values.seo_keywords.join(',')
        : values.seo_keywords || '';
      const ogKeywords = Array.isArray(values.og_keywords)
        ? values.og_keywords.join(',')
        : values.og_keywords || '';
      const twitterKeywords = Array.isArray(values.twitter_keywords)
        ? values.twitter_keywords.join(',')
        : values.twitter_keywords || '';
      
      const seo = {
        seo_title: values.seo_title || '',
        seo_description: values.seo_description || '',
        seo_image: values.seo_image || '',
        seo_keywords: seoKeywords,
        og_title: values.og_title || '',
        og_description: values.og_description || '',
        og_image: values.og_image || '',
        og_keywords: ogKeywords,
        twitter_title: values.twitter_title || '',
        twitter_description: values.twitter_description || '',
        twitter_image: values.twitter_image || '',
        twitter_keywords: twitterKeywords,
      };
      const payload = {
        title: values.title,
        content: values.content || '',
        page_type: values.page_type,
        slug: values.slug || null,
        collection_id: values.collection_id || null,
        fk_template_id: values.fk_template_id || null,
        show_header: values.show_header || false,
        show_footer: values.show_footer || false,
        is_custom_link: values.is_custom_link || false,
        is_active: values.is_active !== undefined ? values.is_active : true,
        page_seo_meta: JSON.stringify(seo),
      };

      const res = await pagesService.update(page_id, payload);
      if (res?.data?.status) {
        message.success(res?.data?.message || 'Page updated');
        navigate('/pages');
      } else {
        message.error(res?.data?.message || 'Failed to update page');
      }
    } catch (e) {
      message.error('Error updating page');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Title level={4}>Edit Page</Title>
          </Col>

          <Col xs={24}>
            <Form.Item label="Title" name="title" rules={[{ required: true, message: 'Enter page title' }]}>
              <Input placeholder="Enter page title" />
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Form.Item label="Content" name="content">
              <TextArea rows={4} placeholder="Enter page content" />
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Form.Item label="Code" name="code">
              <TextArea rows={10} placeholder="Paste code or script (optional)" />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item label="Page Type" name="page_type" rules={[{ required: true }]}>
              <Select placeholder="Select page type">
                {PAGE_TYPES.map((t) => (
                  <Select.Option key={t} value={t}>{t}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item label="Slug" name="slug">
              <Input placeholder="Enter page slug" />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item label="Collection ID" name="collection_id">
              <Input placeholder="Enter collection ID" />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item label="Template" name="fk_template_id">
              <Select placeholder="Select template" allowClear showSearch optionFilterProp="children">
                {templates.map((tpl) => (
                  <Select.Option key={tpl.temp_id || tpl.id} value={tpl.temp_id || tpl.id}>
                    {tpl.temp_name || tpl.name || `Template #${tpl.temp_id || tpl.id}`}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} md={6}>
            <Form.Item label="Show Header" name="show_header" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item label="Show Footer" name="show_footer" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item label="Custom Link" name="is_custom_link" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item label="Active" name="is_active" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
        </Row>

        {/* SEO Blocks */}
        <Card size="small" style={{ marginTop: 8 }} title="SEO">
          <Row gutter={[16, 8]}>
            <Col xs={24} md={12}>
              <Form.Item label="SEO Title" name="seo_title">
                <Input placeholder="Enter SEO title" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="SEO Keywords" name="seo_keywords">
                <Select
                  mode="tags"
                  placeholder="Type a keyword and press Enter"
                  tokenSeparators={[","]}
                  open={false}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="SEO Description" name="seo_description">
                <Input placeholder="Enter SEO Description" />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item label="SEO Image" name="seo_image">
                <Input type="hidden" />
              </Form.Item>
              {seoImage && (
                <div style={{ marginBottom: 12 }}>
                  <div
                    style={{
                      padding: "8px 12px",
                      background: "#fff7e6",
                      border: "1px solid #ffd591",
                      borderRadius: 6,
                      color: "#fa8c16",
                      marginBottom: 8,
                    }}
                  >
                    <Text strong>{getImageUrl(seoImage)}</Text>
                  </div>
                  <div
                    style={{
                      width: "100%",
                      height: "120px",
                      border: "1px solid #d9d9d9",
                      borderRadius: 6,
                      overflow: "hidden",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "#fafafa",
                      marginBottom: 8,
                    }}
                  >
                    <img
                      src={getImageUrl(seoImage)}
                      alt="SEO preview"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                      }}
                    />
                  </div>
                </div>
              )}
              <ImgCrop aspect={1200 / 630} rotationSlider quality={1}>
                <Upload
                  accept="image/*"
                  showUploadList={false}
                  beforeUpload={(file) => {
                    handleSeoImageUpload(file, "seo_image");
                    return false;
                  }}
                >
                  <Button
                    icon={<UploadOutlined />}
                    style={{ width: "100%" }}
                  >
                    {seoImage ? "Change SEO Image" : "Upload SEO Image"}
                  </Button>
                </Upload>
              </ImgCrop>
            </Col>
          </Row>
        </Card>

        <Card size="small" style={{ marginTop: 8 }} title="Facebook SEO">
          <Row gutter={[16, 8]}>
            <Col xs={24} md={12}>
              <Form.Item label="Facebook SEO Title" name="og_title">
                <Input placeholder="Enter Facebook title" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Facebook SEO Keywords" name="og_keywords">
                <Select
                  mode="tags"
                  placeholder="Type a keyword and press Enter"
                  tokenSeparators={[","]}
                  open={false}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Facebook SEO Description" name="og_description">
                <Input placeholder="Enter Facebook Description" />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item label="Facebook Image" name="og_image">
                <Input type="hidden" />
              </Form.Item>
              {ogImage && (
                <div style={{ marginBottom: 12 }}>
                  <div
                    style={{
                      padding: "8px 12px",
                      background: "#fff7e6",
                      border: "1px solid #ffd591",
                      borderRadius: 6,
                      color: "#fa8c16",
                      marginBottom: 8,
                    }}
                  >
                    <Text strong>{getImageUrl(ogImage)}</Text>
                  </div>
                  <div
                    style={{
                      width: "100%",
                      height: "120px",
                      border: "1px solid #d9d9d9",
                      borderRadius: 6,
                      overflow: "hidden",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "#fafafa",
                      marginBottom: 8,
                    }}
                  >
                    <img
                      src={getImageUrl(ogImage)}
                      alt="Facebook preview"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                      }}
                    />
                  </div>
                </div>
              )}
              <ImgCrop aspect={1200 / 630} rotationSlider quality={1}>
                <Upload
                  accept="image/*"
                  showUploadList={false}
                  beforeUpload={(file) => {
                    handleSeoImageUpload(file, "og_image");
                    return false;
                  }}
                >
                  <Button
                    icon={<UploadOutlined />}
                    style={{ width: "100%" }}
                  >
                    {ogImage ? "Change Facebook Image" : "Upload Facebook Image"}
                  </Button>
                </Upload>
              </ImgCrop>
            </Col>
          </Row>
        </Card>

        <Card size="small" style={{ marginTop: 8 }} title="Twitter SEO">
          <Row gutter={[16, 8]}>
            <Col xs={24} md={12}>
              <Form.Item label="Twitter SEO Title" name="twitter_title">
                <Input placeholder="Enter Twitter title" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Twitter SEO Keywords" name="twitter_keywords">
                <Select
                  mode="tags"
                  placeholder="Type a keyword and press Enter"
                  tokenSeparators={[","]}
                  open={false}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Twitter SEO Description" name="twitter_description">
                <Input placeholder="Enter SEO Description" />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item label="Twitter Image" name="twitter_image">
                <Input type="hidden" />
              </Form.Item>
              {twitterImage && (
                <div style={{ marginBottom: 12 }}>
                  <div
                    style={{
                      padding: "8px 12px",
                      background: "#fff7e6",
                      border: "1px solid #ffd591",
                      borderRadius: 6,
                      color: "#fa8c16",
                      marginBottom: 8,
                    }}
                  >
                    <Text strong>{getImageUrl(twitterImage)}</Text>
                  </div>
                  <div
                    style={{
                      width: "100%",
                      height: "120px",
                      border: "1px solid #d9d9d9",
                      borderRadius: 6,
                      overflow: "hidden",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "#fafafa",
                      marginBottom: 8,
                    }}
                  >
                    <img
                      src={getImageUrl(twitterImage)}
                      alt="Twitter preview"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                      }}
                    />
                  </div>
                </div>
              )}
              <ImgCrop aspect={1200 / 600} rotationSlider quality={1}>
                <Upload
                  accept="image/*"
                  showUploadList={false}
                  beforeUpload={(file) => {
                    handleSeoImageUpload(file, "twitter_image");
                    return false;
                  }}
                >
                  <Button
                    icon={<UploadOutlined />}
                    style={{ width: "100%" }}
                  >
                    {twitterImage ? "Change Twitter Image" : "Upload Twitter Image"}
                  </Button>
                </Upload>
              </ImgCrop>
            </Col>
          </Row>
        </Card>

        <Form.Item style={{ marginTop: 16 }}>
          <Row gutter={12}>
            <Col>
              <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={loading}>Save</Button>
            </Col>
            <Col>
              <Button onClick={() => navigate('/pages')} icon={<ArrowLeftOutlined />}>Cancel</Button>
            </Col>
          </Row>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default EditPage;
